/**
 * ImageModal — fullscreen click-to-enlarge overlay for images and videos.
 *
 * Shared UX for every "click the asset to see it bigger" surface in the app:
 * the campaign map, scene background, party hero image, character portrait,
 * and (eventually) avatars. Consolidates the portal + backdrop + Escape-key
 * pattern so new render sites don't need to re-implement it.
 */

import { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import ManagedVideo from './ManagedVideo';

export interface ImageModalProps {
  /** Whether the modal is currently visible. */
  isOpen: boolean;
  /** Called when the user dismisses the modal (backdrop click, close button, Escape). */
  onClose: () => void;
  /**
   * Image URL. Ignored if `videoUrl` is provided.
   * At least one of `imageUrl` / `videoUrl` must be set while the modal is open.
   */
  imageUrl?: string;
  /** Video URL. Takes precedence over `imageUrl` when both are present. */
  videoUrl?: string;
  /** Accessible label describing the asset (also drives `alt` on images). */
  altText?: string;
}

/**
 * Renders a dark-backdrop overlay with the asset centered at up to 95vw/95vh.
 * Clicking the backdrop closes, clicking the asset itself does not.
 */
export default function ImageModal({
  isOpen,
  onClose,
  imageUrl,
  videoUrl,
  altText = '',
}: ImageModalProps) {
  const handleClose = useCallback(() => onClose(), [onClose]);

  // Dismiss on Escape when the modal is open.
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, handleClose]);

  if (!isOpen || (!imageUrl && !videoUrl)) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 cursor-pointer"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-label={altText || 'Enlarged image'}
    >
      <button
        className="absolute top-4 right-4 z-10 rounded-full bg-black/60 p-2 text-white hover:bg-black/80 transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          handleClose();
        }}
        aria-label="Close"
      >
        <X className="h-6 w-6" />
      </button>
      <div
        className="relative max-w-[95vw] max-h-[95vh] flex items-center justify-center cursor-default"
        onClick={(e) => e.stopPropagation()}
      >
        {videoUrl ? (
          <ManagedVideo
            src={videoUrl}
            className="max-w-[95vw] max-h-[95vh] object-contain rounded-lg"
          />
        ) : (
          <img
            src={imageUrl!}
            alt={altText}
            className="max-w-[95vw] max-h-[95vh] object-contain rounded-lg"
          />
        )}
      </div>
    </div>,
    document.body
  );
}
